import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api, type MeResponse } from './api';

const SESSION_KEY = ['session'] as const;

/**
 * Source de vérité de la session côté front. Le cookie étant httpOnly, on ne
 * peut pas le lire : on demande au serveur qui nous sommes. Un 401 signifie
 * simplement « pas connecté » et ne doit pas être traité comme une panne.
 */
export function useSession() {
  const query = useQuery<MeResponse | null>({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await api<MeResponse>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    session: query.data ?? null,
    isLoading: query.isPending,
    isError: query.isError,
  };
}

export function useLogin() {
  // Rechargement COMPLET après connexion, symétrique de la déconnexion. Se
  // contenter d'invalider ['session'] laissait survivre en cache le catalogue et
  // les lignes d'un compte précédent (même clé de cache pour admin et rep,
  // staleTime de 5 min sur les lignes) : un rep pouvait alors voir les lignes
  // d'un autre. Le rechargement repart d'un cache vide, sous la nouvelle identité.
  const rentrer = () => window.location.assign('/');
  return useMutation({
    mutationFn: (creds: { username: string; password: string }) =>
      api<{ user: MeResponse['user'] }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(creds),
      }),
    onSuccess: rentrer,
  });
}

export function useLogout() {
  // Rechargement COMPLET de la page après déconnexion. Le cookie de session
  // vient d'être effacé côté serveur, donc l'app repart d'elle-même sur l'écran
  // de connexion. C'est plus fiable que de vider le cache React Query en
  // espérant que l'écran se rafraîchisse (ce qui ne se produisait pas), et ça
  // garantit qu'aucune donnée d'un rep ne survit en mémoire sur un iPad partagé.
  const rentrer = () => window.location.assign('/');
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
    onSuccess: rentrer,
    onError: rentrer,
  });
}
